import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Surface elevation.
 * - `card` (defaut) : surface plate, elev-1 du brand book (shadow-card, rounded-xl).
 * - `panel` : fenetre flottante, elev-panel (shadow-panel, rounded-2xl).
 */
export type CardVariant = 'card' | 'panel';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Niveau de surface. `card` = carte plate, `panel` = panneau flottant. */
  variant?: CardVariant;
  /**
   * Carte cliquable : ajoute l'elevation au survol (elev-hover) et un leger
   * lift. A reserver aux surfaces reellement interactives.
   */
  interactive?: boolean;
}

const variantClasses: Record<CardVariant, string> = {
  // elev-1 : surface plate. radius 18px, ombre basse et large, bordure neutre.
  card: 'rounded-xl border border-border bg-surface text-ink-900 shadow-card',
  // elev-panel : fenetre flottante. radius 24px, ombre profonde a deux couches.
  panel: 'rounded-2xl border border-border bg-surface text-ink-900 shadow-panel',
};

/**
 * Conteneur de surface MyTaki, theme via les design tokens (zero hex en dur).
 * Composable avec CardHeader / CardTitle / CardDescription / CardContent / CardFooter.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, variant = 'card', interactive = false, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'font-sans',
        variantClasses[variant],
        interactive &&
          'cursor-pointer transition-all duration-150 hover:-translate-y-px hover:border-border-strong hover:shadow-hover',
        className,
      )}
      {...props}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return <div ref={ref} className={cn('flex flex-col gap-1.5 p-6', className)} {...props} />;
  },
);

export const CardTitle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardTitle({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'font-sans text-lg font-semibold leading-none tracking-tight text-ink-900',
          className,
        )}
        {...props}
      />
    );
  },
);

export const CardDescription = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardDescription({ className, ...props }, ref) {
    return (
      <div ref={ref} className={cn('font-sans text-sm text-ink-500', className)} {...props} />
    );
  },
);

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />;
  },
);
